import { Api, DateUtil, ModelRow } from "@mcleod/core";
import {
    Button, ChangeEvent, Checkbox, ClickEvent, ContextMenuCreationEvent, ContextMenuLabelProps, DataSourceAction,
    DataSourceExecutionEvent, Image, Label, Layout, Panel, SaveButton, SlideoutDecorator, Snackbar, TableRow,
    TableRowDisplayEvent, TableRowMode, TableRowMoveEvent, Textbox, Toast
} from "@mcleod/components";
import { ImageName } from "@mcleod/images";
import { AutogenLayoutEquipmentAssignments } from "./autogen/AutogenLayoutEquipmentAssignments";
import { getDispatchControlBoolean, getDispatchControlValue } from "@mcleod/general";
import { ManifestMovementQuickInfo } from "../ManifestMovementQuickInfo";
import { PlanningUtil } from "../planning/PlanningUtil";
import { StopUtil } from "../StopUtil";
import { UnassignDialog } from "../UnassignDialog";
import { YSplit } from "../YSplit";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { OrderTabView } from "../OrderTabView";

interface EquipmentAssignmentProps {
    equipmentId: string;
    movementId: string;
    tractorId: string;
    equipmentType?: string;
    doAfterSlideout?: () => void;
}

export class EquipmentAssignments extends AutogenLayoutEquipmentAssignments {
    private movementId: string;
    private equipmentId: string;
    private tractorId: string;
    private equipmentType: string;
    public saveButton: SaveButton | Button;
    public isPreassigning: boolean = false;
    public unassignedOpenedMove: boolean = false;

    static showSlideout(props: EquipmentAssignmentProps) {
        const self = Layout.getLayout("lme/dispatch/asset/EquipmentAssignments") as EquipmentAssignments;
        const { equipmentId, tractorId, movementId, equipmentType } = props;
        const saveButton = SaveButton.create({ caption: "Save", rowBreak: false, enabled: false, onClick: () => self.saveResequence() });
        const slideout = new SlideoutDecorator({
            layout: self,
            width: "80%",
            fillHeight: true,
            title: "Manage Assignments",
            overlayProps: { greyedBackground: true },
            onClose: () => self.slideOut(),
            addlComponents: [saveButton],
            doAfterSlideIn: () => {
                self.setup(movementId, equipmentId, tractorId, equipmentType);
                self.saveButton = saveButton;
                self.mainDataSource.addHasChangedComponent(saveButton);
                self.addLayoutDataLoadListener(() => self.setupSlideoutHeader(slideout));
            },
            doAfterSlideOut: () => props.doAfterSlideout?.()
        });
    }

    private setupSlideoutHeader(slideout: SlideoutDecorator) {
        if (this.activeRow) {
            const label = new Label({ text: `-  ${this.activeRow.get("equipment")}`, rowBreak: false, fontSize: "large", fontBold: true });
            slideout.panelHeader.insert(label, 1);
        }
    }

    onLoad(): void | Promise<void> {
        this.setAssignmentCaption(true);
    }

    private setAssignmentCaption(useDispatchBy: boolean) {
        let caption: string;
        if (useDispatchBy) {
            caption = this.getEquipmentTypeCaption();
        }
        this.textboxEquipment.caption = caption;
    }

    getEquipmentTypeCaption(equipmentType: string = null) {
        switch (equipmentType ?? getDispatchControlValue("dispatch_by")) {
            case "T": return "Tractor Name";
            case "D": return "Driver Name";
        }
    }

    async setup(movementId: string, equipmentId: string, tractor_id: string, equipmentType: string = null) {
        this.movementId = movementId;
        this.equipmentId = equipmentId;
        this.tractorId = tractor_id;
        this.equipmentType = equipmentType;
        await this.search({ equipment_id: equipmentId, equipment_type: equipmentType });
    }

    async saveResequence() {
        try {
            if (await this.preassignmentCountChanged()) {
                CommonDialogs.showError("These preassignments were updated by another user. Please try again.");
                this.search({ equipment_id: this.equipmentId, equipment_type: this.equipmentType });
                return;
            }

            if (this.tableEquipmentAssignments.data.some(row => row.get("unassigned", "N") == "Y")) {
                if (!(await this.promptUnassign())) return;
            }

            if (this.tableEquipmentAssignments.dataSource.hasChanged() &&
                    this.tableEquipmentAssignments.data.length > 0) {
                this.saveButton.busy = true;
                await Api.post("lme/dispatch/asset/resequence-assignments", {
                    assignments: JSON.stringify(this.tableEquipmentAssignments.data.map(row => row.data))
                });
            }
        } finally {
            this.saveButton.busy = false;
            this.search({ equipment_id: this.equipmentId, equipment_type: this.equipmentType });
        }
    }

    async promptUnassign(): Promise<boolean> {
        const resultRow = getDispatchControlBoolean("req_unassign_reason", false)
            ? await new UnassignDialog("B").show() : true;
        if (resultRow) {
            try {
                this.saveButton.busy = true;
                await this.unassign(resultRow);
                this.tableEquipmentAssignments.data = this.tableEquipmentAssignments.data.filter(row => row.get("unassigned", "N") == "N");
                return true;
            } catch {
                Snackbar.showWarningSnackbar("Failed to unassign order(s)");
            } finally {
                this.saveButton.busy = false;
            }
        }
        return false;
    }

    async unassign(resultRow: ModelRow | boolean) {
        const selectedRows = this.tableEquipmentAssignments.rows.filter(row => row.data.get("unassigned", "N") == "Y");
        await Api.post("lme/dispatch/asset/unassign-equipment/batch", {
            "keys": selectedRows.map(row => ({ movement_id: row.data.get("movement_id") })),
            "data": {
                "unassign_reason": resultRow instanceof ModelRow ? resultRow : null
            }
        });
        if (selectedRows.length == this.tableEquipmentAssignments.rowCount ||
             selectedRows.some(row => row.data.get("movement_id") == this.movementId)) {
            this.unassignedOpenedMove = true;
        }
    }

    async preassignmentCountChanged(): Promise<boolean> {
        const results = await Api.search(this.sourcePreassignments.url, { equipment_id: this.equipmentId, equipment_type: this.equipmentType });
        const dataCount = results.data ? results.data.length : 0;
        return dataCount != this.tableEquipmentAssignments.rowCount;
    }

    /** This is an event handler for the onTableRowMove event of tableEquipmentAssignments. */
    async tableEquipmentAssignmentsAfterTableRowMove(event: TableRowMoveEvent) {
        let priorStop = null;
        for (const tableRow of this.tableEquipmentAssignments.rows) {
            const moveRow = tableRow.data;
            moveRow.set("calc_dist_from_prev", null);
            if (priorStop != null) { // skip first stop
                const distCalcProps = {
                    first_city: moveRow.get("first_city_id"),
                    first_state: moveRow.get("first_state"),
                    first_zip: moveRow.get("first_zip"),
                    prior_city: priorStop.city_id,
                    prior_state: priorStop.state,
                    prior_zip: priorStop.zip_code
                };
                const res = await Api.post("lme/dispatch/distance-between-stops", distCalcProps);
                if (res.data?.[0].calc_dist_from_prev) {
                    moveRow.set("calc_dist_from_prev", res.data[0].calc_dist_from_prev);
                }
            }
            priorStop = {
                city_id: moveRow.get("last_city_id"),
                state: moveRow.get("last_state"),
                zip_code: moveRow.get("last_zip"),
            };
            this.tableEquipmentAssignments.redisplaySingleRow(tableRow.index, moveRow, false, true);
        }
    }

    haveRowsMoved(): boolean {
        return this.tableEquipmentAssignments.data.some((row: ModelRow) => row.getChangedData()?.hasOwnProperty("preassign_sequence"));
    }

    tableEquipmentAssignmentsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        tableRow.mode = TableRowMode.UPDATE; // Need to set here so that rowClicked doesn't trigger editRow
        this.tableEquipmentAssignments.redisplaySingleRow(tableRow.index, modelRow, false, true);
        this.updateMilesBetweenStop(tableRow);
        this.updateYsplitImage(tableRow);
        this.updateZmitImage(tableRow);
        this.updateLoadedImage(tableRow);
        this.updateManifestImage(tableRow);
        this.updateLinkedImage(tableRow);
        tableRow.toolsCell.toolsPanel.visible = false;
        this.updateManualToolButtons(tableRow);
        this.setupEtaPanel(tableRow, modelRow);
        const orderId = tableRow.findComponentById("labelOrderNumber") as Label;
        const panelEmptyOrder = tableRow.findComponentById("panelEmptyOrder") as Panel;
        if (!modelRow.isNull("manifest_id")) {
           (tableRow.findComponentById("labelOrderTitle") as Label).text = "Manifest - ";
            orderId.visible = false;
            const manifestIdLabel = tableRow.findComponentById("labelManifestId") as Label;
            manifestIdLabel.visible = true;
            manifestIdLabel.addUnderlineWhenMouseOver();
        }
        else if (!modelRow.isNull("order_id")) {
            orderId.addUnderlineWhenMouseOver();
        }

        if (modelRow.get("loaded") == "E") {
            tableRow.findComponentById("panelOrderNumber")?.setProps({ visible: false });
            panelEmptyOrder.visible = true;
            panelEmptyOrder.findComponentById("labelEmptyOrder")?.addUnderlineWhenMouseOver();
        }

        if ("A" === modelRow.get("status") && modelRow.get("first_actual_arrival") != null)
            tableRow.findComponentById("labelArrivedBadge").visible = true;

        const isOnHold = modelRow.getBoolean("on_hold");
        if (isOnHold) {
            const holdBadge = tableRow.findComponentById("labelHoldBadge");
            if (holdBadge) {
                holdBadge.visible = true;
                CommonTooltips.setTooltipContentTextboxes(holdBadge, () => [{
                    caption: "Reason",
                    text: modelRow.get("hold_reason")
                }]);
            }
        }

        this.setupNextStopWindow(tableRow, modelRow, "sched_arrive_pickup", "sched_late_pickup", "textboxEarlyPickup", "first_timezone");
        this.setupNextStopWindow(tableRow, modelRow, "sched_arrive_delivery", "sched_late_delivery", "textboxEarlyDelivery", "last_timezone");
    }

    panelAssignmentContainerOnContextMenuCreation(event: ContextMenuCreationEvent) {
        if (this.tractorId == null) {
            event.preventDefault();
        }
    }

    private updateManualToolButtons(tableRow: TableRow) {
        const buttonSendLoad = tableRow.findComponentById("buttonSendLoad") as Button;
        buttonSendLoad.visible = this.tractorId != null;
        const dragHandler = tableRow?.toolsCell?.getDragHandlerImage();
        const showDragHandle = dragHandler != null;
        if (showDragHandle) {
            const panelToolButtons = tableRow.findComponentById("panelToolButtons") as Panel;
            panelToolButtons?.add(dragHandler);
        }
    }

    private setupEtaPanel(tableRow: TableRow, modelRow: ModelRow) {
        if (!modelRow) {
            return;
        }
        const labelOriginLateStatus = tableRow.findComponentById("labelLateStatus") as Label;
        const originLateStatus = modelRow.getString("first_late_status");
        const panel = tableRow.findComponentById("panelLateStatus") as Panel;
        const bgColor = modelRow.getString("first_late_color");
        const panelEta = tableRow.findComponentById("panelEta") as Panel;

        panelEta.visible = !modelRow.isNull("first_eta") || !modelRow.isNull("first_late_status");
        StopUtil.setupOriginLateStatusPanel(panel, labelOriginLateStatus, originLateStatus, bgColor);
    }

    private setupNextStopWindow(tableRow: TableRow, modelRow: ModelRow, stopEarlyField: string, stopLateField: string, textboxId: string, timezoneField: string){
            const stopEarly: Date = modelRow.getDateTime(stopEarlyField, null);
            const stopLate: Date = modelRow.getDateTime(stopLateField, null);
            const stopEarlyF: string = DateUtil.formatDateTime(stopEarly, DateUtil.getUserDateTimeFormat());
            const stopLateF: string = DateUtil.formatDateTime(stopLate, DateUtil.getUserDateTimeFormat());
            const timezone: string = modelRow.getString(timezoneField, "");
            let stopWindow: string = "";

            if (stopEarly && stopLate) {
                if (stopEarly.toDateString() === stopLate.toDateString()) {
                    const lateTime = DateUtil.formatTime(stopLate, DateUtil.getUserTimeFormat());
                    stopWindow = `${stopEarlyF} - ${lateTime} ${timezone}`;
                } else {
                    stopWindow = `${stopEarlyF} -\n${stopLateF} ${timezone}`;
                }
            } else if (stopEarly) {
                stopWindow = `${stopEarlyF} ${timezone}`;
            } else if (stopLate) {
                stopWindow = `${stopLateF} ${timezone}`;
            }

            const nextStopWindowLabel = tableRow.findComponentById(textboxId) as Textbox;
            nextStopWindowLabel.text = stopWindow;
        }


    updateMilesBetweenStop(tableRow: TableRow) {
        const distBetweenStops = tableRow.findComponentById("labelDistBetweenStops") as Label;
        distBetweenStops.visible = !tableRow.data.isNull("calc_dist_from_prev");
    }

    private updateYsplitImage(tableRow: TableRow) {
        const modelRow = tableRow.data;
        const imageYsplit = tableRow.findComponentById("imageYsplit") as Image;
        YSplit.setImage(modelRow.get("origin_stop_type"), modelRow.get("dest_stop_type"), imageYsplit);
        imageYsplit.visible = imageYsplit.color != null;
    }

    private updateZmitImage(tableRow: TableRow) {
        const modelRow = tableRow.data;
        const imageZmit = tableRow.findComponentById("imageZmit") as Image;
        const xmitted2driver = modelRow.getBoolean("xmitted2driver");
        const xmitAccepted = modelRow.get("xmit_accepted");
        const imageProps = this.getZMitImageProps(!xmitted2driver, xmitAccepted);
        imageZmit.visible = imageProps.name !== null;
        imageZmit.name = imageProps.name;
        imageZmit.field = imageProps.field;
        imageZmit.displayLabel = imageProps.displayLabel;
        imageZmit.tooltip = imageProps.tooltip;
        imageZmit.color = imageProps.color;
    }

    private getZMitImageProps(zMitNotTransmitted: boolean, zmitAccepted: string): {name:ImageName, field:string, displayLabel:string, tooltip:string, color:string} {
        if (zMitNotTransmitted) {
            return { name: null, field: "xmitted2driver", displayLabel: "Load Not Transmitted", tooltip: "Load Not Transmitted", color: "default"}
        } else if (zmitAccepted == null) {
            return { name: ImageName.zMitTransmitted, field: "xmitted2driver", displayLabel: "Load Transmitted", tooltip: "Load Transmitted", color: "primary"}
        } else if (zmitAccepted === 'Y') {
            return { name: ImageName.zMitTransmittedAccepted, field: "xmit_accepted", displayLabel: "Load Accepted", tooltip: "Load Accepted", color: "success"}
        } else if (zmitAccepted === 'N') {
            return { name: ImageName.zMitTransmittedRejected, field: "xmit_accepted", displayLabel: "Load Rejected", tooltip: "Load Rejected", color: "error"}
        }

        return {name:null, field:"xmitted2driver", displayLabel:null, tooltip:null, color: null};
    }

    private updateLoadedImage(tableRow: TableRow) {
        const loadedImage = tableRow.findComponentById("imageLoaded") as Image;
        const loaded = tableRow.data.get("loaded") == "L";
        loadedImage.name = loaded  ? ImageName.circleCheckEmpty : ImageName.emptyMove;
        loadedImage.tooltip = loaded ? "Loaded" : "Empty";
    }

    private updateManifestImage(tableRow: TableRow) {
        const imageManifest = tableRow.findComponentById("imageManifest") as Image;
        if (imageManifest != null) {
            const manifestId = tableRow.data?.get("manifest_id");
            const movementId = tableRow.data?.get("movement_id");
            imageManifest.visible = manifestId != null;
            ManifestMovementQuickInfo.addHoverToDiscoverToManifestIdComponent(imageManifest, movementId, manifestId);
        }
    }

    private updateLinkedImage(tableRow: TableRow) {
        const imageLinked = tableRow.findComponentById("imageLinked") as Image;
        if (imageLinked != null) {
            const linkedMovementId = tableRow.data?.get("linked_id");
            imageLinked.visible = linkedMovementId != null;
            if (imageLinked.visible) {
                CommonTooltips.setTooltipContentLayout(imageLinked, null, "lme/dispatch/planning/LinkedMovesQuickInfoTable",
                    null, { linked_id: linkedMovementId, selected_movement_id: tableRow.data?.get("movement_id") }
                );
            }
        }
    }

    checkboxUnassignAllOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            this.tableEquipmentAssignments.rows.forEach((row: TableRow) => {
                row.data.set("unassigned", event.newValue ? "Y" : "N");
            });
        }
    }

    checkboxUnassignOnChange(event: ChangeEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Checkbox);
        this.checkboxUnassignAll.checked = this.tableEquipmentAssignments.data.every((row: ModelRow) => row.get("unassigned", "N") == "Y");
        if (event.userInitiatedChange) {
            if (!tableRow.data.isNull("linked_id")) {
                this.tableEquipmentAssignments.rows.forEach(row => {
                    if (row != tableRow && row.data.get("linked_id") == tableRow.data.get("linked_id")) {
                        row.data.set("unassigned", event.newValue ? "Y" : "N");
                    }
                });
            }
        }
    }

    /** This is an event handler for the afterExecution event of sourcePreassignments. */
    sourcePreassignmentsAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            // Do it this way because rearranging issues arise if you assign directly to field
            this.textboxEquipment.displayData(event.dataSource.activeRow, null, 0);
            this.panelActions.visible = this.checkboxUnassignAll.visible = this.sourcePreassignments.data.length > 0;
            this.mainDataSource.notifyHasChangedComponents(false);
        }
    }

    buttonSendLoadOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Button);
        this.sendLoad(tableRow);
    }

    sendLoad(tableRow: TableRow) {
        Api.post("lme/dispatch/asset/zmit-load", { unit_id: null, movement_id: tableRow.data.get("movement_id"), preplan: true })
        .then(() => {
            Toast.showSuccessToast("Load Sent Successfully");
            tableRow.data.set("xmitted2driver", true);
            this.tableEquipmentAssignments.redisplaySingleRow(tableRow.index, tableRow.data, false, true);
        }).catch(e => {
            Snackbar.showWarningSnackbar(e.messages[0]);
        });
    }

    createContextMenuItems(tableRow: TableRow): Partial<ContextMenuLabelProps>[] {
        if (this.tractorId == null) return [];
        return [
            {
                imageName: ImageName.zMit,
                text: "Send Load Preplan",
                onClick: () => this.sendLoad(tableRow),
            }
        ]
    }

    private orderNumberOnClick(event: ClickEvent) {
        PlanningUtil.navigateToOrder(event);
    }

    labelEmptyOrderOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Label);
        const modelRow = tableRow.data;
        OrderTabView.navigateTo({ key: modelRow.get("movement_id"), orderId: modelRow.get("order_id", "none"), newTab: true });
    }
}
